import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api } from "./api";
import type { ImpersonationState, Organization, User } from "./types";
import type { TwoFactorChallengeInfo } from "@/features/auth/two-factor";

type AuthContextValue = {
  user: User | null;
  organizations: Organization[];
  impersonation: ImpersonationState | null;
  loading: boolean;
  refresh: () => Promise<void>;
  patchUser: (partial: Partial<User>) => void;
  /**
   * Password sign-in. Resolves with challenge info when the account has 2FA
   * enabled (no session yet); resolves null once fully signed in.
   */
  signIn: (
    email: string,
    password: string,
  ) => Promise<TwoFactorChallengeInfo | null>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [impersonation, setImpersonation] = useState<ImpersonationState | null>(
    null,
  );
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const me = await api<{
      user: User | null;
      accounts: Organization[];
      impersonation?: ImpersonationState | null;
    }>("/api/auth/me");
    setUser(me.user);
    setOrganizations(
      (me.accounts ?? []).map((a) => ({
        ...a,
        role: a.role ?? "member",
      })),
    );
    setImpersonation(me.impersonation ?? null);
  }, []);

  useEffect(() => {
    void refresh()
      .catch(() => {
        setUser(null);
        setOrganizations([]);
        setImpersonation(null);
      })
      .finally(() => setLoading(false));
  }, [refresh]);

  const signIn = useCallback(
    async (
      email: string,
      password: string,
    ): Promise<TwoFactorChallengeInfo | null> => {
      const result = await api<
        | { user: User }
        | { requiresTwoFactor: true; challengeId: string; methods: string[] }
      >("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      if ("requiresTwoFactor" in result) {
        return {
          challengeId: result.challengeId,
          methods: result.methods,
        };
      }
      await refresh();
      return null;
    },
    [refresh],
  );

  const signOut = useCallback(async () => {
    try {
      await api("/api/auth/logout", { method: "POST" });
    } catch {
      // Clear client session anyway (network/CSRF); server session may already be gone.
    } finally {
      setUser(null);
      setOrganizations([]);
      setImpersonation(null);
    }
  }, []);

  const patchUser = useCallback((partial: Partial<User>) => {
    setUser((current) => (current ? { ...current, ...partial } : current));
  }, []);

  const value = useMemo(
    () => ({
      user,
      organizations,
      impersonation,
      loading,
      refresh,
      patchUser,
      signIn,
      signOut,
    }),
    [
      user,
      organizations,
      impersonation,
      loading,
      refresh,
      patchUser,
      signIn,
      signOut,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function canManageOrg(role: Organization["role"] | undefined) {
  return role === "owner" || role === "admin";
}
